import { hasOptions, isChoice, sectionRuns, type Condition, type Definition, type JumpIf, type Option, type Question, type QuestionType, type Randomizer } from '@survey/core';
import { t } from '../../i18n';
import {
  AlignLeft,
  ArrowDownNarrowWide,
  Calendar,
  CircleDot,
  Gauge,
  Grid3x3,
  Hash,
  Info,
  ListChecks,
  ListCollapse,
  MapPin,
  Paperclip,
  Sigma,
  Signature,
  SlidersHorizontal,
  Star,
  TextCursorInput,
  ToggleLeft,
  Type,
  type LucideIcon,
} from 'lucide-react';

export const TYPE_ICONS: Record<QuestionType, LucideIcon> = {
  short_text: Type,
  long_text: AlignLeft,
  single_choice: CircleDot,
  multiple_choice: ListChecks,
  ranking: ArrowDownNarrowWide,
  dropdown: ListCollapse,
  number: Hash,
  rating: Star,
  date: Calendar,
  yes_no: ToggleLeft,
  matrix: Grid3x3,
  slider: SlidersHorizontal,
  nps: Gauge,
  constant_sum: Sigma,
  text_form: TextCursorInput,
  file_upload: Paperclip,
  signature: Signature,
  location: MapPin,
  descriptive: Info,
};

export function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

const list = (key: 'qe.option_n' | 'qe.row_n' | 'qe.item_n' | 'qe.field_n', n: number, prefix = 'o'): Option[] =>
  Array.from({ length: n }, (_, i) => ({ id: newId(prefix), label: t(key, { n: i + 1 }) }));

export function newQuestion(type: QuestionType): Question {
  const q: Question = { id: newId('q'), type, title: '', required: false };
  if (isChoice(type) || type === 'ranking') q.options = list('qe.option_n', 2);
  if (type === 'matrix') {
    q.rows = list('qe.row_n', 2, 'r');
    q.options = (['qe.agree_1', 'qe.agree_2', 'qe.agree_3', 'qe.agree_4', 'qe.agree_5'] as const).map((k) => ({ id: newId('o'), label: t(k) }));
  }
  if (type === 'constant_sum') q.options = list('qe.item_n', 3);
  if (type === 'text_form') q.options = list('qe.field_n', 2);
  if (type === 'rating') q.scale = { min: 1, max: 5 };
  if (type === 'slider') q.scale = { min: 0, max: 100, step: 1 };
  return q;
}

/** Changes a question's type, keeping what still applies. */
export function changeType(q: Question, type: QuestionType): Question {
  const next: Question = { id: q.id, type, title: q.title, questionCode: q.questionCode, sectionTitle: q.sectionTitle, description: q.description, titleRich: q.titleRich, descriptionRich: q.descriptionRich, tooltip: q.tooltip, textAlign: q.textAlign, media: q.media, required: q.required, visibleIf: q.visibleIf, jumpIf: q.jumpIf, fixedPosition: q.fixedPosition, shuffleWithLogic: q.shuffleWithLogic };
  const fresh = newQuestion(type);
  if (hasOptions(type)) {
    // Choice-like lists carry over between choice types; other lists start fresh.
    const keep = q.options?.length && ((isChoice(q.type) || q.type === 'ranking') === (isChoice(type) || type === 'ranking') || q.type === type);
    next.options = keep ? q.options!.map((o) => (isChoice(type) ? o : { ...o, other: undefined })) : fresh.options;
    next.randomizeOptions = q.randomizeOptions;
    if ((isChoice(type) || type === 'ranking') && q.carryForward) {
      next.carryForward = q.carryForward;
      next.options = undefined;
    }
  }
  if (type === 'matrix') {
    next.rows = q.rows?.length ? q.rows : fresh.rows;
    next.matrixMultiple = q.matrixMultiple;
  }
  if (type === 'rating') next.scale = q.type === 'rating' && q.scale ? q.scale : { min: 1, max: 5, minLabel: q.scale?.minLabel, maxLabel: q.scale?.maxLabel };
  if (type === 'slider') next.scale = q.type === 'slider' && q.scale ? q.scale : fresh.scale;
  if (type === 'nps' && (q.scale?.minLabel || q.scale?.maxLabel)) next.scale = { min: 0, max: 10, minLabel: q.scale.minLabel, maxLabel: q.scale.maxLabel };
  if (type === 'constant_sum') next.total = q.total;
  // Per-answer routes go to the same options when the choices carry over (single, dropdown, multiple):
  // a multiple choice question routes on "contains", the others on "equals".
  if (q.jumpRules?.length && isChoice(q.type) && isChoice(type) && (next.carryForward || (!!next.options?.length && next.options[0]!.id === q.options?.[0]?.id))) {
    const op = type === 'multiple_choice' ? 'contains' : 'equals';
    next.jumpRules = q.jumpRules.map((r) => (r.operator === 'equals' || r.operator === 'contains' ? { ...r, operator: op } : r));
  } else if (q.jumpRules?.length && q.type === type) next.jumpRules = q.jumpRules;
  // Display-only text can't be the target of required answers or logic that reads it.
  if (type === 'descriptive') {
    next.required = false;
    next.jumpIf = undefined;
    next.jumpRules = undefined;
  }
  return next;
}

export function updateQuestion(def: Definition, id: string, patch: Partial<Question> | ((q: Question) => Question)): Definition {
  return {
    ...def,
    questions: def.questions.map((q) => (q.id === id ? (typeof patch === 'function' ? patch(q) : { ...q, ...patch }) : q)),
  };
}

/** Removes a question and any logic, carry-forward, loop or quota condition that depended on it. */
export function removeQuestion(def: Definition, id: string): Definition {
  const sectionOptions = def.settings.sectionOptions && Object.fromEntries(Object.entries(def.settings.sectionOptions).map(([k, o]) => [k, o.loopOver === id ? { ...o, loopOver: undefined } : o]));
  return {
    ...def,
    settings: { ...def.settings, sectionOptions },
    quotas: def.quotas?.map((quota) => ({ ...quota, conditions: quota.conditions.filter((c) => c.questionId !== id) })),
    questions: def.questions
      .filter((q) => q.id !== id)
      .map((q) => {
        const conditions = q.visibleIf?.conditions.filter((c) => c.questionId !== id) ?? [];
        const lostSource = q.carryForward?.questionId === id;
        return {
          ...q,
          visibleIf: q.visibleIf && conditions.length ? { ...q.visibleIf, conditions } : undefined,
          jumpIf: q.jumpIf?.targetQuestionId === id ? undefined : q.jumpIf,
          carryForward: lostSource ? undefined : q.carryForward,
          options: lostSource ? newQuestion(q.type).options : q.options,
        };
      }),
  };
}

export function moveQuestion(def: Definition, id: string, delta: number): Definition {
  const idx = def.questions.findIndex((q) => q.id === id);
  const target = idx + delta;
  if (idx < 0 || target < 0 || target >= def.questions.length) return def;
  const questions = def.questions.slice();
  const [q] = questions.splice(idx, 1);
  questions.splice(target, 0, q!);
  return { ...def, questions };
}

/** Where to place the dragged question relative to its drop target. */
export type DropPos = 'before' | 'after';

/**
 * Moves a question next to a drop-target question (drag and drop in the
 * outline). Works between sections as well: the question adopts the title of
 * the run it lands in, so membership follows position instead of splitting a
 * section with a stray title. Unused section options are cleaned up.
 */
export function moveQuestionTo(def: Definition, id: string, targetId: string, pos: DropPos): Definition {
  if (id === targetId) return def;
  const without = def.questions.slice();
  const idx = without.findIndex((q) => q.id === id);
  if (idx < 0) return def;
  const [q] = without.splice(idx, 1);
  if (!q) return def;
  const to = without.findIndex((x) => x.id === targetId);
  if (to < 0) return def;
  // The run holding the target after the removal is the section being joined.
  const runs = sectionRuns({ ...def, questions: without });
  const runTitle = runs.find((r) => r.questions.some((x) => x.id === targetId))?.title ?? '';
  const insert = pos === 'before' ? to : to + 1;
  const moved: Question = { ...q, sectionTitle: runTitle || undefined };
  const questions = [...without.slice(0, insert), moved, ...without.slice(insert)];
  return tidySectionOptions({ ...def, questions });
}

export function duplicateQuestion(def: Definition, id: string): { def: Definition; newId: string } {
  const idx = def.questions.findIndex((q) => q.id === id);
  const src = def.questions[idx];
  if (!src) return { def, newId: id };
  const copy: Question = {
    ...structuredClone(src),
    id: newId('q'),
    questionCode: undefined,
    jumpIf: undefined,
    title: src.title ? `${src.title} (copy)` : '',
    options: src.options?.map((o) => ({ ...o, id: newId('o') })),
    rows: src.rows?.map((o) => ({ ...o, id: newId('r') })),
  };
  const questions = def.questions.slice();
  questions.splice(idx + 1, 0, copy);
  return { def: { ...def, questions }, newId: copy.id };
}

/** Ids of the run of consecutive questions sharing a section title, starting at `fromId`. */
function runFrom(def: Definition, fromId: string): string[] {
  const start = def.questions.findIndex((q) => q.id === fromId);
  if (start < 0) return [];
  const title = def.questions[start]!.sectionTitle?.trim() ?? '';
  const ids: string[] = [];
  for (let i = start; i < def.questions.length && (def.questions[i]!.sectionTitle?.trim() ?? '') === title; i++) ids.push(def.questions[i]!.id);
  return ids;
}

function setTitle(def: Definition, ids: string[], title: string): Definition {
  const set = new Set(ids);
  const t = title.trim() || undefined;
  return { ...def, questions: def.questions.map((q) => (set.has(q.id) ? { ...q, sectionTitle: t } : q)) };
}

/**
 * Drops per-section shuffle options and randomizer members for titles no longer
 * used; optionally moves one title to a new name. A randomizer left without
 * sections goes away, and its count never exceeds its sections.
 */
function tidySectionOptions(def: Definition, rename?: { from: string; to: string }): Definition {
  const opts = { ...def.settings.sectionOptions };
  if (rename && opts[rename.from] && rename.to && !opts[rename.to]) opts[rename.to] = opts[rename.from]!;
  const used = new Set(def.questions.map((q) => q.sectionTitle?.trim()).filter(Boolean));
  for (const k of Object.keys(opts)) if (!used.has(k)) delete opts[k];
  const randomizers = (def.settings.randomizers ?? [])
    .map((r) => {
      const sections = r.sections.map((t) => (rename && rename.to && t === rename.from ? rename.to : t)).filter((t) => used.has(t));
      return { ...r, sections, present: Math.min(Math.max(r.present, 1), Math.max(sections.length, 1)) };
    })
    .filter((r) => r.sections.length > 0);
  return {
    ...def,
    settings: { ...def.settings, sectionOptions: Object.keys(opts).length ? opts : undefined, randomizers: randomizers.length ? randomizers : undefined },
  };
}

/**
 * Starts a new section at `fromId`, like a section break: that question and the
 * following questions that shared its old section move into the new one.
 */
export function startSection(def: Definition, fromId: string, title: string): Definition {
  return tidySectionOptions(setTitle(def, runFrom(def, fromId), title));
}

/** Renames the section run whose first question is `firstId`. */
export function renameSection(def: Definition, firstId: string, title: string): Definition {
  const from = def.questions.find((q) => q.id === firstId)?.sectionTitle?.trim() ?? '';
  return tidySectionOptions(setTitle(def, runFrom(def, firstId), title), { from, to: title.trim() });
}

/** Removes the section run whose first question is `firstId`; its questions keep their place. */
export function removeSection(def: Definition, firstId: string): Definition {
  return tidySectionOptions(setTitle(def, runFrom(def, firstId), ''));
}

/** Questions whose conditions, jumps, carried options or loop depend on this one. */
export function dependents(def: Definition, id: string): Question[] {
  const loops = new Set(Object.entries(def.settings.sectionOptions ?? {}).filter(([, o]) => o.loopOver === id).map(([k]) => k));
  return def.questions.filter(
    (q) =>
      q.visibleIf?.conditions.some((c) => c.questionId === id) ||
      q.jumpIf?.targetQuestionId === id ||
      q.carryForward?.questionId === id ||
      loops.has(q.sectionTitle?.trim() ?? ''),
  );
}

/** A copy of a question for another questionnaire: new ids, and no links to questions that aren't there. */
export function importedQuestion(src: Question): Question {
  const { visibleIf: _v, jumpIf: _j, carryForward, loop: _l, ...rest } = structuredClone(src);
  return {
    ...rest,
    id: newId('q'),
    questionCode: undefined,
    options: carryForward ? newQuestion(src.type).options : rest.options,
  };
}

// ---------------------------------------------------------------------------
// Sections. A section is a run of questions sharing a title; every question is
// meant to live in one, so adding a question always names its section.
// ---------------------------------------------------------------------------

const titleOf = (q: Question): string => q.sectionTitle?.trim() ?? '';

/** Section titles in survey order, each once. */
export function sectionTitles(def: Definition): string[] {
  const out: string[] = [];
  for (const q of def.questions) {
    const t = titleOf(q);
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
}

/** `base`, or `base 2`, `base 3`… when that title is taken. */
export function uniqueSectionTitle(def: Definition, base: string): string {
  const used = new Set(sectionTitles(def));
  const root = base.trim();
  if (!used.has(root)) return root;
  for (let n = 2; ; n++) if (!used.has(`${root} ${n}`)) return `${root} ${n}`;
}

/**
 * Adds questions to a section: after `afterId` when that question is in the
 * section, otherwise at the end of the section. A section that doesn't exist
 * yet is created at the end of the survey.
 */
export function addToSection(def: Definition, qs: Question[], section: string, afterId?: string): Definition {
  const title = section.trim();
  const added = qs.map((q) => ({ ...q, sectionTitle: title || undefined }));
  const after = afterId ? def.questions.findIndex((q) => q.id === afterId) : -1;
  let at = def.questions.length;
  if (after >= 0 && titleOf(def.questions[after]!) === title) at = after + 1;
  else {
    const last = def.questions.reduce((acc, q, i) => (titleOf(q) === title ? i : acc), -1);
    if (last >= 0) at = last + 1;
  }
  const questions = def.questions.slice();
  questions.splice(at, 0, ...added);
  return { ...def, questions };
}

/** Index just past the last question of the flow element (section or randomizer group) holding `title`. */
function endOfElement(def: Definition, title: string): number {
  const el = flowElements(def).find((e) => (e.kind === 'section' ? e.section.title === title : e.sections.some((s) => s.title === title)));
  if (!el) return def.questions.length;
  const ids = (el.kind === 'section' ? el.section.questions : el.sections.flatMap((s) => s.questions)).map((q) => q.id);
  return def.questions.findIndex((q) => q.id === ids[ids.length - 1]) + 1;
}

/**
 * A new section with one blank starter question, placed after the flow element
 * holding `afterTitle` (after a whole randomizer group, never inside one), or
 * at the end of the survey. The title is made unique.
 */
export function newSection(def: Definition, title: string, afterTitle?: string): { def: Definition; title: string; firstId: string } {
  const unique = uniqueSectionTitle(def, title || 'Section');
  const starter: Question = { ...newQuestion('short_text'), sectionTitle: unique };
  const at = afterTitle ? endOfElement(def, afterTitle) : def.questions.length;
  const questions = def.questions.slice();
  questions.splice(at, 0, starter);
  return { def: { ...def, questions }, title: unique, firstId: starter.id };
}

/** Renames every question's section `from` to `to` (which must not be another section's title). */
export function renameSectionTitle(def: Definition, from: string, to: string): Definition {
  const next = to.trim();
  if (!next || next === from || sectionTitles(def).includes(next)) return def;
  const questions = def.questions.map((q) => (titleOf(q) === from ? { ...q, sectionTitle: next } : q));
  const randomizers = def.settings.randomizers?.map((r) => (r.after === from ? { ...r, after: next } : r));
  return tidySectionOptions({ ...def, questions, settings: randomizers ? { ...def.settings, randomizers } : def.settings }, { from, to: next });
}

// ---------------------------------------------------------------------------
// Logic seen from the question it reads. Display logic is written on the
// question that gets shown, so without this the source question has no sign
// of it.
// ---------------------------------------------------------------------------

export interface LogicLink {
  kind: 'shows' | 'carries';
  target: Question;
  /** The conditions on this question (empty for carry-forward). */
  conditions: Condition[];
  match: 'all' | 'any';
}

/** What other questions do with this question's answer: show themselves, or carry its options. */
export function logicFrom(def: Definition, id: string): LogicLink[] {
  const out: LogicLink[] = [];
  for (const q of def.questions) {
    if (q.id === id) continue;
    const conditions = q.visibleIf?.conditions.filter((c) => c.questionId === id) ?? [];
    if (conditions.length) out.push({ kind: 'shows', target: q, conditions, match: q.visibleIf?.match ?? 'all' });
    if (q.carryForward?.questionId === id) out.push({ kind: 'carries', target: q, conditions: [], match: 'all' });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Survey flow. The flow is the list of sections, with randomizers grouping
// adjacent sections. Everything the flow editor does is one of these pure
// functions, so it can be tested without the UI.
// ---------------------------------------------------------------------------

export interface FlowSection {
  title: string;
  questions: Question[];
}

export type FlowElement = { kind: 'section'; section: FlowSection } | { kind: 'randomizer'; randomizer: Randomizer; sections: FlowSection[] };

/** The flow as elements in order. Member sections that aren't adjacent show as a second group (the checker reports it). */
export function flowElements(def: Definition): FlowElement[] {
  const owner = new Map<string, Randomizer>();
  for (const r of def.settings.randomizers ?? []) for (const t of r.sections) if (!owner.has(t)) owner.set(t, r);
  const out: FlowElement[] = [];
  for (const run of sectionRuns(def)) {
    const section = { title: run.title, questions: run.questions };
    const r = run.title ? owner.get(run.title) : undefined;
    const last = out[out.length - 1];
    if (r && last?.kind === 'randomizer' && last.randomizer.id === r.id) last.sections.push(section);
    else if (r) out.push({ kind: 'randomizer', randomizer: r, sections: [section] });
    else out.push({ kind: 'section', section });
  }
  // A randomizer with no sections yet has no place among the questions: it sits under the section it was added
  // below (or at the end when that one is gone).
  for (const r of def.settings.randomizers ?? []) {
    if (r.sections.length) continue;
    const el: FlowElement = { kind: 'randomizer', randomizer: r, sections: [] };
    const holds = (e: FlowElement) => (e.kind === 'section' ? e.section.title === r.after : e.sections.some((x) => x.title === r.after));
    const host = r.after ? out.findIndex(holds) : -1;
    if (host < 0) out.push(el);
    else {
      let at = host + 1;
      for (let e = out[at]; e?.kind === 'randomizer' && !e.sections.length; e = out[at]) at++; // keep blank ones in the order they were added
      out.splice(at, 0, el);
    }
  }
  return out;
}

function rebuild(def: Definition, elements: FlowElement[]): Definition {
  const questions = elements.flatMap((e) => (e.kind === 'section' ? e.section.questions : e.sections.flatMap((s) => s.questions)));
  const randomizers = (def.settings.randomizers ?? []).map((r) => {
    const el = elements.find((e): e is Extract<FlowElement, { kind: 'randomizer' }> => e.kind === 'randomizer' && e.randomizer.id === r.id);
    if (!el) return r;
    const { after: _after, ...rest } = r;
    const sections = el.sections.map((s) => s.title);
    return sections.length ? { ...rest, sections } : { ...r, sections };
  });
  return { ...def, questions, settings: { ...def.settings, randomizers: randomizers.length ? randomizers : undefined } };
}

const swap = <T,>(list: T[], i: number, j: number): T[] => {
  if (i < 0 || j < 0 || i >= list.length || j >= list.length) return list;
  const out = list.slice();
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
};

/** Moves a section one place up (-1) or down (+1): within its randomizer, or past the neighbouring element. */
export function moveSection(def: Definition, title: string, delta: -1 | 1): Definition {
  const els = flowElements(def);
  const at = els.findIndex((e) => (e.kind === 'section' ? e.section.title === title : e.sections.some((s) => s.title === title)));
  const el = els[at];
  if (!el) return def;
  if (el.kind === 'randomizer') {
    const i = el.sections.findIndex((s) => s.title === title);
    const sections = swap(el.sections, i, i + delta);
    return sections === el.sections ? def : rebuild(def, els.map((e, k) => (k === at ? { ...el, sections } : e)));
  }
  const moved = swap(els, at, at + delta);
  return moved === els ? def : rebuild(def, moved);
}

/** Moves a whole randomizer group one place up or down. */
export function moveRandomizer(def: Definition, id: string, delta: -1 | 1): Definition {
  const els = flowElements(def);
  const at = els.findIndex((e) => e.kind === 'randomizer' && e.randomizer.id === id);
  const self = els[at];
  if (self?.kind === 'randomizer' && !self.sections.length) {
    // A blank randomizer only remembers which section it sits under.
    const rest = els.filter((_, i) => i !== at);
    const to = Math.max(0, Math.min(rest.length, at + delta));
    const host = rest[to - 1];
    const title = host ? (host.kind === 'section' ? host.section.title : host.sections[host.sections.length - 1]?.title) : undefined;
    if (!title || to === at) return def;
    return { ...def, settings: { ...def.settings, randomizers: def.settings.randomizers!.map((r) => (r.id === id ? { ...r, after: title } : r)) } };
  }
  const moved = swap(els, at, at + delta);
  return moved === els ? def : rebuild(def, moved);
}

/** Index of the flow element holding the section. */
function elementOf(els: FlowElement[], title: string): number {
  return els.findIndex((e) => (e.kind === 'section' ? e.section.title === title : e.sections.some((s) => s.title === title)));
}

/**
 * Drag and drop of a section next to another section.
 * - Both in the same randomizer: reorders inside it.
 * - Otherwise it lands before/after the whole flow element holding the target
 *   (a randomizer group counts as one element, so dropping next to one of its
 *   sections never joins it; use "include section" for that).
 * - A randomizer member dropped outside its group leaves the randomizer.
 */
export function moveSectionTo(def: Definition, title: string, targetTitle: string, pos: DropPos): Definition {
  if (!title || !targetTitle || title === targetTitle) return def;
  const els = flowElements(def);
  const from = elementOf(els, title);
  if (from < 0 || elementOf(els, targetTitle) < 0) return def;
  const src = els[from]!;

  if (src.kind === 'randomizer') {
    const to = els[elementOf(els, targetTitle)]!;
    if (to.kind === 'randomizer' && to.randomizer.id === src.randomizer.id) {
      const moved = src.sections.find((x) => x.title === title)!;
      const sections = src.sections.filter((x) => x.title !== title);
      sections.splice(sections.findIndex((x) => x.title === targetTitle) + (pos === 'after' ? 1 : 0), 0, moved);
      return rebuild(def, els.map((e, i) => (i === from ? { ...src, sections } : e)));
    }
  }

  let moved: FlowSection;
  let rest: FlowElement[];
  if (src.kind === 'section') {
    moved = src.section;
    rest = els.filter((_, i) => i !== from);
  } else {
    moved = src.sections.find((x) => x.title === title)!;
    const left = src.sections.filter((x) => x.title !== title);
    rest = els.flatMap((e, i) => (i !== from ? [e] : left.length ? [{ ...src, sections: left }] : []));
  }
  const at = elementOf(rest, targetTitle);
  rest.splice(pos === 'before' ? at : at + 1, 0, { kind: 'section', section: moved });
  let next = rebuild(def, rest);
  if (src.kind === 'randomizer') {
    const randomizers = next.settings.randomizers?.map((r) => (r.id === src.randomizer.id ? { ...r, sections: r.sections.filter((t) => t !== title) } : r));
    next = { ...next, settings: { ...next.settings, randomizers } };
  }
  return tidySectionOptions(next);
}

/**
 * Drag and drop of a section from outside (or from another randomizer) into a randomizer, next to
 * `targetTitle` (one of its sections) or at its end. The section joins the group and is shown at random.
 */
export function moveSectionIntoRandomizer(def: Definition, title: string, randomizerId: string, targetTitle: string | null, pos: DropPos): Definition {
  const rz = def.settings.randomizers?.find((r) => r.id === randomizerId);
  if (!title || !rz || rz.sections.includes(title)) return def;
  const els = flowElements(def);
  const from = elementOf(els, title);
  if (from < 0) return def;
  const src = els[from]!;
  const moved = src.kind === 'section' ? src.section : src.sections.find((x) => x.title === title)!;
  const rest = els.flatMap((e, i): FlowElement[] => {
    if (i !== from) return [e];
    if (src.kind === 'section') return [];
    const left = src.sections.filter((x) => x.title !== title);
    return left.length ? [{ ...src, sections: left }] : [];
  });
  const at = rest.findIndex((e) => e.kind === 'randomizer' && e.randomizer.id === randomizerId);
  const target = rest[at];
  if (!target || target.kind !== 'randomizer') return def;
  const sections = [...target.sections];
  const ti = targetTitle ? sections.findIndex((x) => x.title === targetTitle) : -1;
  sections.splice(ti < 0 ? sections.length : pos === 'before' ? ti : ti + 1, 0, moved);
  rest[at] = { ...target, sections };
  let next = rebuild(def, rest);
  const order = sections.map((x) => x.title);
  const randomizers = (next.settings.randomizers ?? [])
    .map((r) => {
      if (r.id === randomizerId) return { ...r, sections: order };
      return { ...r, sections: r.sections.filter((t) => t !== title) };
    })
    .filter((r) => r.sections.length > 0)
    .map((r) => ({ ...r, present: Math.min(r.present, r.sections.length) }));
  next = { ...next, settings: { ...next.settings, randomizers: randomizers.length ? randomizers : undefined } };
  return tidySectionOptions(next);
}

/** Moves a question to the end of a section (drag and drop onto the section itself). */
export function moveQuestionToSection(def: Definition, id: string, title: string): Definition {
  const q = def.questions.find((x) => x.id === id);
  if (!q || !title) return def;
  const rest = def.questions.filter((x) => x.id !== id);
  const last = rest.reduce((acc, x, i) => (titleOf(x) === title ? i : acc), -1);
  if (last < 0) return def;
  const questions = [...rest.slice(0, last + 1), { ...q, sectionTitle: title }, ...rest.slice(last + 1)];
  return tidySectionOptions({ ...def, questions });
}

/** Drag and drop of a whole randomizer next to the flow element holding `targetTitle` (not onto itself). */
export function moveRandomizerTo(def: Definition, randomizerId: string, targetTitle: string, pos: DropPos): Definition {
  const els = flowElements(def);
  const from = els.findIndex((e) => e.kind === 'randomizer' && e.randomizer.id === randomizerId);
  const to = elementOf(els, targetTitle);
  if (from < 0 || to < 0 || from === to) return def;
  const moved = els[from]!;
  const rest = els.filter((_, i) => i !== from);
  const at = elementOf(rest, targetTitle);
  rest.splice(pos === 'before' ? at : at + 1, 0, moved);
  return rebuild(def, rest);
}

/**
 * Copies a section with all its questions, right after the original (and into
 * the same randomizer). Questions get new ids and the copies' own logic points
 * at the copies; logic aimed outside the section keeps its target.
 */
export function duplicateSection(def: Definition, title: string): { def: Definition; title: string } {
  const run = def.questions.filter((q) => titleOf(q) === title);
  if (!run.length) return { def, title };
  const copyTitle = uniqueSectionTitle(def, `${title} (copy)`);
  const ids = new Map(run.map((q) => [q.id, newId('q')]));
  const remap = (id: string) => ids.get(id) ?? id;
  const copies = run.map((q): Question => {
    const c = structuredClone(q);
    return {
      ...c,
      id: ids.get(q.id)!,
      questionCode: undefined,
      sectionTitle: copyTitle,
      visibleIf: c.visibleIf && { ...c.visibleIf, conditions: c.visibleIf.conditions.map((k) => ({ ...k, questionId: remap(k.questionId) })) },
      jumpIf: c.jumpIf && { ...c.jumpIf, targetQuestionId: c.jumpIf.targetQuestionId && remap(c.jumpIf.targetQuestionId) },
      carryForward: c.carryForward && { ...c.carryForward, questionId: remap(c.carryForward.questionId) },
    };
  });
  const at = def.questions.findIndex((q) => q.id === run[run.length - 1]!.id) + 1;
  const questions = def.questions.slice();
  questions.splice(at, 0, ...copies);
  const sectionOptions = def.settings.sectionOptions?.[title] ? { ...def.settings.sectionOptions, [copyTitle]: structuredClone(def.settings.sectionOptions[title]!) } : def.settings.sectionOptions;
  const randomizers = def.settings.randomizers?.map((r) => {
    const i = r.sections.indexOf(title);
    return i < 0 ? r : { ...r, sections: [...r.sections.slice(0, i + 1), copyTitle, ...r.sections.slice(i + 1)] };
  });
  return { def: { ...def, questions, settings: { ...def.settings, sectionOptions, randomizers } }, title: copyTitle };
}

/** Deletes a section and its questions, with the logic that depended on them. */
export function deleteSection(def: Definition, title: string): Definition {
  let next = def;
  for (const q of def.questions) if (titleOf(q) === title) next = removeQuestion(next, q.id);
  return tidySectionOptions(next);
}

/** A randomizer around two new sections, after the flow element holding `afterTitle` (or at the end). */
export function addRandomizer(def: Definition, afterTitle?: string): { def: Definition; randomizerId: string } {
  // Starts blank: sections are added to it, or dragged in, afterwards.
  const randomizer: Randomizer = { id: newId('rnd'), sections: [], present: 1, evenly: true, ...(afterTitle ? { after: afterTitle } : {}) };
  return { def: { ...def, settings: { ...def.settings, randomizers: [...(def.settings.randomizers ?? []), randomizer] } }, randomizerId: randomizer.id };
}

/** Adds a new section to the end of a randomizer. */
export function addSectionToRandomizer(def: Definition, randomizerId: string, title = 'Section'): { def: Definition; title: string; firstId: string } | null {
  const r = def.settings.randomizers?.find((x) => x.id === randomizerId);
  if (!r) return null;
  const made = newSection(def, title, r.sections[r.sections.length - 1]);
  const randomizers = def.settings.randomizers!.map((x) => (x.id === randomizerId ? { ...x, sections: [...x.sections, made.title] } : x));
  return { def: { ...made.def, settings: { ...made.def.settings, randomizers } }, title: made.title, firstId: made.firstId };
}

/** The section directly above or below a randomizer, when it is a plain titled section that can join. */
export function neighbourSection(def: Definition, randomizerId: string, side: 'above' | 'below'): string | null {
  const els = flowElements(def);
  const at = els.findIndex((e) => e.kind === 'randomizer' && e.randomizer.id === randomizerId);
  const next = els[at + (side === 'above' ? -1 : 1)];
  return next?.kind === 'section' && next.section.title ? next.section.title : null;
}

/** Pulls the adjacent plain section above or below into the randomizer. */
export function absorbSection(def: Definition, randomizerId: string, side: 'above' | 'below'): Definition {
  const title = neighbourSection(def, randomizerId, side);
  if (!title) return def;
  const randomizers = def.settings.randomizers!.map((r) => (r.id === randomizerId ? { ...r, sections: side === 'above' ? [title, ...r.sections] : [...r.sections, title] } : r));
  return { ...def, settings: { ...def.settings, randomizers } };
}

/** Takes the first or last section out of its randomizer (middle ones would split the group). */
export function releaseSection(def: Definition, randomizerId: string, title: string): Definition {
  const r = def.settings.randomizers?.find((x) => x.id === randomizerId);
  if (!r || (r.sections[0] !== title && r.sections[r.sections.length - 1] !== title)) return def;
  return tidySectionOptions({ ...def, settings: { ...def.settings, randomizers: def.settings.randomizers!.map((x) => (x.id === randomizerId ? { ...x, sections: x.sections.filter((t) => t !== title) } : x)) } });
}

/** Ungroups a randomizer; its sections stay where they are and are always shown. */
export function removeRandomizer(def: Definition, randomizerId: string): Definition {
  const randomizers = def.settings.randomizers?.filter((r) => r.id !== randomizerId);
  return { ...def, settings: { ...def.settings, randomizers: randomizers?.length ? randomizers : undefined } };
}

/** Evenly present, per-section limits and the count reset of a randomizer; empty limits are dropped. */
export function setRandomizerLimits(def: Definition, randomizerId: string, patch: Pick<Randomizer, 'evenly' | 'limits' | 'resetAt'>): Definition {
  const randomizers = def.settings.randomizers?.map((r) => {
    if (r.id !== randomizerId) return r;
    const { evenly: _e, limits: _l, resetAt: _t, ...rest } = r;
    const limits = patch.limits && Object.keys(patch.limits).length ? patch.limits : undefined;
    return { ...rest, ...(patch.evenly ? { evenly: true } : {}), ...(limits ? { limits } : {}), ...(patch.resetAt ? { resetAt: patch.resetAt } : {}) };
  });
  return { ...def, settings: { ...def.settings, randomizers } };
}

/** How many of its sections a randomizer shows each respondent (kept between 1 and the number of sections). */
export function setRandomizerCount(def: Definition, randomizerId: string, present: number): Definition {
  const randomizers = def.settings.randomizers?.map((r) => (r.id === randomizerId ? { ...r, present: Math.min(Math.max(Math.round(present) || 1, 1), Math.max(r.sections.length, 1)) } : r));
  return { ...def, settings: { ...def.settings, randomizers } };
}

/** The randomizer a section belongs to, if any. */
export function randomizerOfSection(def: Definition, title: string): Randomizer | undefined {
  return title ? def.settings.randomizers?.find((r) => r.sections.includes(title)) : undefined;
}

// ---------------------------------------------------------------------------
// Branches in the survey flow: "if question A's answer is B, go to question C,
// section D or the end". They are the questions' jump rules, seen from the flow.
// ---------------------------------------------------------------------------

export interface Branch {
  source: Question;
  /** -1 for the question's `jumpIf`, otherwise the index in `jumpRules`. */
  index: number;
  rule: JumpIf;
}

/** Every branch, in question order. */
export function branchesOf(def: Definition): Branch[] {
  return def.questions.flatMap((q) => [
    ...(q.jumpIf ? [{ source: q, index: -1, rule: q.jumpIf }] : []),
    ...(q.jumpRules ?? []).map((rule, index) => ({ source: q, index, rule })),
  ]);
}

/** Adds a branch to a question; one with the same condition is replaced. */
export function addBranch(def: Definition, sourceId: string, rule: JumpIf): Definition {
  return updateQuestion(def, sourceId, (q) => {
    const same = (r: JumpIf) => r.operator === rule.operator && JSON.stringify(r.value ?? null) === JSON.stringify(rule.value ?? null);
    return { ...q, jumpRules: [...(q.jumpRules ?? []).filter((r) => !same(r)), rule] };
  });
}

export function removeBranch(def: Definition, sourceId: string, index: number): Definition {
  return updateQuestion(def, sourceId, (q) => {
    if (index < 0) return { ...q, jumpIf: undefined };
    const rules = (q.jumpRules ?? []).filter((_, i) => i !== index);
    return { ...q, jumpRules: rules.length ? rules : undefined };
  });
}

/** Adds an ending to send respondents to from a branch. */
export function addEnding(def: Definition, name: string, message: string): { def: Definition; id: string } {
  const id = newId('end');
  return { def: { ...def, endings: [...(def.endings ?? []), { id, name, message }] }, id };
}
